import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar, ChevronDown, Building2 } from 'lucide-react';
import SearchableFilterSelect from '../common/SearchableFilterSelect.js';
import { locationHubService } from '../../services/locationHubService.js';
import userService from '../../services/UserService.js';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import {
  getStoredAnalyticsHub,
  getStoredAnalyticsUser,
  setStoredAnalyticsHub,
  setStoredAnalyticsUser,
} from './common/analyticsParams.js';

const SCOPE_ROLE_KEY = 'analytics_scope_role';

const readSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const sessionRole = (user) => user?.role_name || user?.role?.role_name || user?.role || '';

const initialAnalyticsScope = () => {
  const user = readSessionUser();
  const role = sessionRole(user);
  const ownHub = user?.location_hub_id ? String(user.location_hub_id) : '';
  if (role === 'Agent') {
    if (ownHub) setStoredAnalyticsHub(ownHub);
    setStoredAnalyticsUser('');
    try { sessionStorage.setItem(SCOPE_ROLE_KEY, 'Agent'); } catch { /* ignore */ }
    return { hub: ownHub, user: '' };
  }
  let stamped = '';
  try { stamped = sessionStorage.getItem(SCOPE_ROLE_KEY) || ''; } catch { /* ignore */ }
  if (stamped !== role) {
    setStoredAnalyticsHub('');
    setStoredAnalyticsUser('');
    try { sessionStorage.setItem(SCOPE_ROLE_KEY, role); } catch { /* ignore */ }
    return { hub: '', user: '' };
  }
  return { hub: getStoredAnalyticsHub(), user: getStoredAnalyticsUser() };
};

const FILTER_OPTIONS = [
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Compact date-range dropdown used across analytics pages
 * (same pattern as shipment commission dashboard).
 */
const DateFilterDropdown = ({
  filter,
  setFilter,
  customDates,
  setCustomDates,
  onApply,
  dateRange,
  currentUser = null,
  companies = [],
  selectedCompany = null,
  onCompanyChange = null,
  showCompanyFilter = false,
}) => {
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedHub, setSelectedHub] = useState(() => initialAnalyticsScope().hub);
  const [selectedUser, setSelectedUser] = useState(() => initialAnalyticsScope().user);
  const [selectedOption, setSelectedOption] = useState(filter);
  const dateDropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);

  const getFilterLabel = () => {
    const option = FILTER_OPTIONS.find((opt) => opt.value === filter);
    return option ? option.label : 'Select Period';
  };

  const getCompanyLabel = () => {
    if (!selectedCompany || selectedCompany === 'all') return 'All Companies';
    const company = companies.find((c) => c.id === parseInt(selectedCompany, 10));
    return company ? company.company_name : 'Select Company';
  };

  const actor = currentUser || readSessionUser();
  const actorRole = actor?.role_name || actor?.role?.role_name || actor?.role;
  const isAgent = actorRole === 'Agent';
  const ownHubId = actor?.location_hub_id ? String(actor.location_hub_id) : '';
  const locationLocked = isAgent && Boolean(ownHubId);

  const shouldShowCompanyFilter = () => {
    if (!currentUser || !showCompanyFilter) return false;
    const userRole = currentUser.role_name || currentUser.role?.role_name || currentUser.role;
    return userRole === 'System Admin';
  };

  useEffect(() => {
    setSelectedOption(filter);
  }, [filter]);

  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  useEffect(() => {
    if (!locationLocked) return;
    setSelectedHub(ownHubId);
    setStoredAnalyticsHub(ownHubId);
    setSelectedUser('');
    setStoredAnalyticsUser('');
  }, [locationLocked, ownHubId]);

  useEffect(() => {
    let cancelled = false;
    locationHubService.getHubsForSelect()
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.hubs || response?.data?.data || [];
        setHubs(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setHubs([]);
      });
    userService.getAll(1, 500, { all: 'true' })
      .then((payload) => {
        if (cancelled) return;
        const rows = payload?.data || [];
        setUsers(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setIsDateDropdownOpen(false);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (value) => {
    if (value === 'custom') {
      setSelectedOption(value);
      return;
    }
    setSelectedOption(value);
    setCustomDates({ from_date: '', to_date: '' });
    setFilter(value);
    setIsDateDropdownOpen(false);
    if (onApply) onApply();
  };

  const handleCustomApply = () => {
    if (!customDates.from_date || !customDates.to_date) return;
    setFilter('custom');
    setIsDateDropdownOpen(false);
    if (onApply) onApply();
  };

  const userLocation = (person) => (
    person?.location_hub_label
    || person?.location_hub_name
    || person?.city
    || 'No location'
  );

  const visibleUsers = useMemo(() => {
    if (!selectedHub) return users;
    return users.filter((person) => String(person.location_hub_id) === String(selectedHub));
  }, [users, selectedHub]);

  const applyScope = () => {
    if (onApply) onApply();
  };

  const handleHubChange = (hubId) => {
    const next = hubId ? String(hubId) : '';
    setSelectedHub(next);
    setStoredAnalyticsHub(next);
    if (selectedUser) {
      const person = users.find((item) => String(item.user_id) === String(selectedUser));
      if (next && person && String(person.location_hub_id) !== next) {
        setSelectedUser('');
        setStoredAnalyticsUser('');
      }
    }
    applyScope();
  };

  const handleUserChange = (userId) => {
    const next = userId ? String(userId) : '';
    setSelectedUser(next);
    setStoredAnalyticsUser(next);
    applyScope();
  };

  const handleCompanyChange = (companyId) => {
    if (onCompanyChange) {
      onCompanyChange(companyId);
      setIsCompanyDropdownOpen(false);
    }
  };

  const triggerClass =
    'flex items-center gap-2 px-3 md:px-4 py-2 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700';

  const hubOptions = [
    { value: '', label: 'All locations' },
    ...hubs.map((hub) => ({
      value: String(hub.id),
      label: formatHubBranchLabel(hub) || hub.location || 'Location',
    })),
  ];
  const userOptions = [
    { value: '', label: 'All users' },
    ...visibleUsers.map((person) => ({
      value: String(person.user_id),
      label: `${person.name} (${userLocation(person)})`,
    })),
  ];

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
      <div className="w-[180px] min-w-0" title={locationLocked ? 'Location is locked to your branch' : 'Location'}>
        <SearchableFilterSelect
          value={selectedHub}
          disabled={locationLocked}
          placeholder="All locations"
          emptyLabel="All locations"
          options={locationLocked
            ? hubOptions.filter((option) => option.value === '' || option.value === ownHubId)
            : hubOptions}
          onChange={(event) => handleHubChange(event.target.value)}
        />
      </div>
      <div className="w-[220px] min-w-0">
        <SearchableFilterSelect
          value={selectedUser}
          placeholder="All users"
          emptyLabel="All users"
          options={userOptions}
          onChange={(event) => handleUserChange(event.target.value)}
        />
      </div>
      {shouldShowCompanyFilter() && (
        <div className="relative" ref={companyDropdownRef}>
          <button
            type="button"
            onClick={() => {
              setIsCompanyDropdownOpen(!isCompanyDropdownOpen);
              setIsDateDropdownOpen(false);
            }}
            className={triggerClass}
          >
            <Building2 className="w-4 h-4 shrink-0 text-slate-500" />
            <span className="max-w-[140px] sm:max-w-[180px] truncate">{getCompanyLabel()}</span>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isCompanyDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {isCompanyDropdownOpen && (
            <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto">
              <div className="p-2">
                <button
                  type="button"
                  onClick={() => handleCompanyChange('all')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !selectedCompany || selectedCompany === 'all'
                      ? 'bg-sky-50 text-sky-700 font-medium'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  All Companies
                </button>
                <div className="border-t border-gray-200 my-2" />
                {companies.map((company) => (
                  <button
                    type="button"
                    key={company.id}
                    onClick={() => handleCompanyChange(company.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      String(selectedCompany) === String(company.id)
                        ? 'bg-sky-50 text-sky-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="font-medium">{company.company_name}</div>
                    <div className="text-xs text-gray-500">{company.company_id}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="relative" ref={dateDropdownRef}>
        <button
          type="button"
          onClick={() => {
            setIsDateDropdownOpen(!isDateDropdownOpen);
            setIsCompanyDropdownOpen(false);
          }}
          className={triggerClass}
        >
          <Calendar className="w-4 h-4 shrink-0 text-slate-500" />
          <span className="whitespace-nowrap">{getFilterLabel()}</span>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${isDateDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDateDropdownOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Select Date Range</h3>

              <div className="space-y-1 mb-4">
                {FILTER_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => handleFilterChange(option.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedOption === option.value
                        ? 'bg-sky-50 text-sky-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {selectedOption === 'custom' && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={customDates.from_date || ''}
                    onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 mb-3"
                  />

                  <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={customDates.to_date || ''}
                    onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })}
                    min={customDates.from_date || undefined}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 mb-3"
                  />

                  <button
                    type="button"
                    onClick={handleCustomApply}
                    disabled={!customDates.from_date || !customDates.to_date}
                    className="w-full px-4 py-2 bg-sky-50 text-sky-700 border border-sky-200 rounded-xl text-sm font-medium hover:bg-sky-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply Custom Range
                  </button>
                </div>
              )}

              {dateRange?.start_date && dateRange?.end_date && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Selected Period:</span>
                    <br />
                    {new Date(dateRange.start_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {' - '}
                    {new Date(dateRange.end_date).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {dateRange.days != null && (
                      <>
                        <br />
                        <span className="text-gray-500">({dateRange.days} days)</span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilterDropdown;
